import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Activity, ArrowRight } from 'lucide-react';

import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import type { Detection, Item, Scanner, Branch } from '@packages/shared-types';
import { listItems } from '@/services/itemService';
import { listScanners } from '@/services/scannerService';
import { listBranches } from '@/services/branchService';

interface ActivityFeedProps {
  detections: Detection[];
  isLoading?: boolean;
}

interface ActivityItem {
  detection: Detection;
  itemName: string;
  scannerName: string;
  branchName: string;
  timeAgo: string;
}

export function ActivityFeed({
  detections,
  isLoading,
}: ActivityFeedProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const unknownItemLabel = useIndustryTranslation('pages.dashboard.activityFeed.unknownItem');
  const unknownBranchLabel = useIndustryTranslation('pages.dashboard.activityFeed.unknownBranch');
  const [items, setItems] = useState<Item[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        const [itemsData, scannersData, branchesData] = await Promise.all([
          listItems(),
          listScanners(),
          listBranches(),
        ]);
        setItems(itemsData);
        setScanners(scannersData);
        setBranches(branchesData);
      } catch (err) {
        console.error('Failed to load activity feed data', err);
      } finally {
        setIsLoadingData(false);
      }
    };
    void loadData();
  }, []);

  const activityItems = useMemo((): ActivityItem[] => {
    if (isLoadingData || items.length === 0 || scanners.length === 0 || branches.length === 0) {
      return [];
    }

    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentDetections = detections
      .filter((d) => new Date(d.detectedAt) > twentyFourHoursAgo)
      .sort((a, b) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime())
      .slice(0, 15);

    return recentDetections.map((detection) => {
      const item = items.find((i) => i.id === detection.itemId);
      const scanner = scanners.find((s) => s.id === detection.scannerId);
      const branch = branches.find((b) => b.id === detection.branchId);

      return {
        detection,
        itemName: item?.name || unknownItemLabel,
        scannerName: scanner?.name || 'Unknown Scanner',
        branchName: branch?.name || unknownBranchLabel,
        timeAgo: formatDistanceToNow(new Date(detection.detectedAt), { addSuffix: true }),
      };
    });
  }, [detections, items, scanners, branches, isLoadingData]);

  const handleItemClick = useCallback(
    (itemId: string) => {
      navigate(`/items/${itemId}`);
    },
    [navigate],
  );

  if (isLoading || isLoadingData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('pages.dashboard.activityFeed.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3 py-8">
          <LoadingLogo size="sm" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('pages.dashboard.activityFeed.title')}</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/detections')}
          className="text-xs"
        >
          {t('pages.dashboard.activityFeed.viewAll')}
          <ArrowRight className="ml-1 h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent>
        {activityItems.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('pages.dashboard.activityFeed.noActivity')}
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {activityItems.map((activity) => (
              <div
                key={activity.detection.id}
                onClick={() => handleItemClick(activity.detection.itemId)}
                className="flex items-start gap-3 rounded-lg p-2 hover:bg-muted/50 cursor-pointer transition-colors"
              >
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Activity className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{activity.itemName}</p>
                  <p className="text-xs text-muted-foreground">
                    {activity.scannerName} • {activity.branchName}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{activity.timeAgo}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
